import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { requestSingleFaq } from "src/redux/singleFaq/singleFaq.actions";
import { stripInlineStyles } from "src/utils";

// Component Imports
import ActiveLink from "src/components/layouts/ActiveLink";
import Skeleton from "src/components/elements/Skeleton";
import Error from "pages/_error";
import HeadWrapper from "src/components/layouts/HeadWrapper";
import Feedback from "src/components/elements/feedback/Feedback";
import BreadCrumb from "src/components/layouts/BreadCrumb";

// Utility Imports
import urlGenerator from "src/utils/urlGenerator";
import { youtubeEmbedUrlGenerator, isEmptyObject } from "src/utils";

// Style Imports
import styles from "src/styles/pages/faq.module.scss";
import shareStyles from "src/styles/components/elements/share.module.scss";
import useModal from "src/components/hooks/useModal";
import { faqFeedback } from "src/utils/api";

const Sharer = ({ faqTitle }) => {
    const { show, RenderModal } = useModal();
    let currentUrl = typeof window !== "undefined" ? window.location.href : "";

    const share = () => {
        show();
    };

    const emailShareUrl = `https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=&su=Your+Subject+here&body=${currentUrl}&ui=2&tf=1&pli=1`;

    return (
        <>
            <RenderModal customSizeClass={`${shareStyles.share_sheet} share_sheet`}>
                <div className={shareStyles.list_share_icons}>
                    <ul>
                        <h3>SHARE FAQ</h3>
                        <li className={shareStyles.deskNone}>
                            <a href={`whatsapp://send?text=${currentUrl}`}>
                                <span className={`${shareStyles.whatsapp_btn} ${shareStyles.share_icon_btn}`}></span>
                                Whatsapp
                            </a>
                        </li>
                        <li>
                            <a href={`https://www.facebook.com/sharer.php?u=${currentUrl}`}>
                                <span className={`${shareStyles.facebook_btn} ${shareStyles.share_icon_btn}`}></span>
                                Facebook
                            </a>
                        </li>
                        <li className={shareStyles.deskNone}>
                            <a href={`https://t.me/share/url?url=${currentUrl}`}>
                                <span className={`${shareStyles.telegram_btn} ${shareStyles.share_icon_btn}`}></span>
                                Telegram
                            </a>
                        </li>
                        <li className={shareStyles.deskNone}>
                            <a href={`sms:?&body=${currentUrl}`}>
                                <span className={`${shareStyles.sms_btn} ${shareStyles.share_icon_btn}`}></span>
                                SMS
                            </a>
                        </li>
                        <li>
                            <a href={`https://twitter.com/intent/tweet?url=${currentUrl}`}>
                                <span className={`${shareStyles.twitter_btn} ${shareStyles.share_icon_btn}`}></span>
                                Twitter
                            </a>
                        </li>
                        <li onClick={() => window.open(emailShareUrl, "sharer", "toolbar=0,status=0,width=648,height=395")}>
                            <a>
                                <span className={`${shareStyles.email_btn} ${shareStyles.share_icon_btn}`}></span>
                                Email
                            </a>
                        </li>
                    </ul>
                </div>
            </RenderModal>

            <span className={styles.share_link} onClick={share}>
                <img src="/images/share_btn.png" alt="Share FAQ" />
            </span>
        </>
    )
}
    


Sharer.displayName = "Sharer";

const Faq = ({ faqSlug, categorySlug }) => {
    const dispatch = useDispatch();

    // Access Redux state
    const faqsData = useSelector((state) => state.faqs[faqSlug]);
    const faq = faqsData?.payload?.data?.faq;
    const relatedFaqs = faqsData?.payload?.data?.relatedFaqs;

    // Handle Image Loading Errors
    const [imageLoadError, setImageLoadError] = useState(false);

    useEffect(() => {
        if (!faq) {
            dispatch(requestSingleFaq({ params: { slug: faqSlug, categorySlug } }));
        }
    }, [faqSlug, categorySlug, dispatch]);

    const faqTitle = faq?.title;
    const faqVideo = faq?.video ? youtubeEmbedUrlGenerator(faq.video) : null;
    const categoryName = faq?.categories[0]?.label || "Category";

    const breadCrumbInfo = [
        { itemName: "Home", itemLink: "/" },
        {
            itemName: categoryName.length > 30 ? categoryName.slice(0, 30) + "..." : categoryName,
            itemLink: urlGenerator.categoryLink(categorySlug),
        },
        {
            itemName: faqTitle?.length > 30 ? faqTitle.slice(0, 30) + "..." : faqTitle
        },
    ];

    if (!isEmptyObject(faqsData?.payload) && faqsData?.payload?.statusCode !== 200 && !faqsData?.isLoading) {
        return <Error statusCode={faqsData?.payload?.statusCode} />;
    }

    return (
        <>
            <HeadWrapper
                title={faq?.metaTitle || faqTitle}
                desc={faq?.description}
                keywords={faq?.keywords}
                canonical={`https://support.freecharge.in/${categorySlug}/${faqSlug}`}
            />
            <div className={styles.wrapper_detail}>
                <BreadCrumb breadCrumbInfo={breadCrumbInfo} />
                <div className={styles.container2}>
                    <div className={styles.images_comment_section}>
                        <div className={styles.images_left}>
                            <div className={styles.image_title}>
                                <h1 className="ucfirst">{faqTitle || <Skeleton width={200} />}</h1>
                            </div>
                            <div className={styles.image_content}>
                                {faq?.featuredImage && (
                                    <div className={styles.detail_banner}>
                                        <img
                                            src={faq.featuredImage}
                                            alt="FAQ"
                                            className={`${styles.featuredImage} ${imageLoadError ? styles.hidden : ""}`.trim()}
                                            onError={() => setImageLoadError(true)}
                                        />
                                    </div>
                                )}
                                {faq?.answer ? (
                                    <div className={styles.images_para} dangerouslySetInnerHTML={{ __html: stripInlineStyles(faq.answer) }} />
                                ) : (
                                    <Skeleton count={5} />
                                )}
                                {faqVideo && (
                                    <iframe
                                        width="100%"
                                        height="315"
                                        src={faqVideo}
                                        frameBorder="0"
                                        className={styles.iframe}
                                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                )}
                                <div className={styles.info_bar2}>
                                    {/* {faq && faq.updatedAt ? (
                    <>
                      <span className={styles.info_title2}>Last Updated: </span>

                      <span className={styles.info_date2}>
                        {humanDate(faq.updatedAt)}
                      </span>
                    </>
                  ) : (
                      <>
                        <span className={styles.info_date2}>
                          <Skeleton width={75} />
                        </span>
                      </>
                    )} */}
                                    <div className={styles.mobile_view1}>
                                        <Sharer title={faq && faq.title ? faq.title : undefined} />
                                    </div>
                                </div>

                                <hr />
                                <div className={styles.reaction_bar}>
                                    <span className={styles.like_faq}>Did this answer your question</span>
                                    <Feedback contentId={faq?._id} storage="faqIds" url={faqFeedback} />
                                    <div className={styles.desktop_view}>
                                        <Sharer
                                            title={faq && faq.title ? faq.title : undefined}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {relatedFaqs?.length > 0 && (
                        <div className={styles.faq_right}>
                            <div className={styles.helpHead2}>
                                <p className={styles.title_head2}>Related Question</p>
                            </div>
                            <div className={styles.color_divider1}></div>
                            <div className={styles.link_topic}>
                                <ul>
                                    {relatedFaqs.map(({ title, _id, slug, categories }) => (
                                        <li key={_id}>
                                            <p className="ucfirst">
                                                <ActiveLink
                                                    href={urlGenerator.faqDir()}
                                                    as={urlGenerator.faqLink(categories[0]?.slug, slug)}
                                                >
                                                    <a>{title?.length > 80 ? title.slice(0, 80) + "..." : title}</a>
                                                </ActiveLink>
                                            </p>
                                            {_id !== relatedFaqs.length - 1 && (
                                                <div className={styles.grey_divider1}></div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Faq;

// ✅ Use `getServerSideProps` for SSR
export async function getServerSideProps({ query }) {
    const faqSlug = query.faqSlug;
    const categorySlug = query.categorySlug;

    return {
        props: {
            faqSlug,
            categorySlug,
        },
    };
}
